import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useClearRefinements } from 'react-instantsearch';
import { RefinementDropdown } from './RefinementDropdown';
import RefinementEmptyList from './RefinementEmptyList';
import { RefinementSearchInput } from '~/features/productList/components/RefinementSearchInput';
import { FilterSelectionHeader } from '~/features/productList/components/FilterSelectionHeader';
import { RefinementListRow } from '~/features/productList/components/RefinementListRow';
import {
    AlphabeticVirtualizedList,
    AlphabeticVirtualizedListHandle,
    LetterPicker,
} from '~/features/productList/components/AlphabeticRefinementList';
import { useAlphabeticGroups } from '~/features/productList/hooks/useAlphabeticGroups';
import { RefinementProps } from './RefinementProps';
import useOptimizedRefinementList from '~/shared/hooks/useOptimizedRefinementList';
import { useDrawerFilterTracking } from '~/features/productList/hooks/useDrawerFilterTracking';
import styles from '~/features/productList/components/AlphabeticRefinementList/AlphabeticVirtualizedList.module.scss';

type RefinementAlphabeticListDropdown = RefinementProps & {
    searchable?: boolean;
    showCount?: boolean;
    searchPlaceholder?: string;
};

export const RefinementAlphabeticListDropdown: FC<RefinementAlphabeticListDropdown> = ({
    attribute,
    searchable,
    showCount = true,
    label,
    searchPlaceholder,
    isVisible = true,
}: RefinementAlphabeticListDropdown) => {
    const [searchValue, setSearchValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const listRef = useRef<AlphabeticVirtualizedListHandle>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    useEffect(() => {
        const node = headerRef.current;
        if (!node) return;

        const observer = new ResizeObserver(([entry]) => {
            const height = Math.round(entry.contentRect.height);
            setHeaderHeight((prev) => (prev === height ? prev : height));
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    const { trackFilter } = useDrawerFilterTracking();
    const { allItems, refine } = useOptimizedRefinementList({ attribute });
    const { refine: clearSelection } = useClearRefinements({ includedAttributes: [attribute] });
    const groups = useAlphabeticGroups(allItems, searchValue, isOpen);
    const letters = useMemo(() => groups.sections.map((section) => section.letter), [groups]);
    const selectedCount = allItems.filter((item) => item.isRefined).length;

    const onClose = useCallback((open: boolean) => {
        setIsOpen(open);
        if (!open) setSearchValue('');
    }, []);

    const handleRefine = useCallback(
        (value: string) => {
            if (!value) return;
            trackFilter(attribute, value, {
                isRefined: allItems.find((item) => item.value === value)?.isRefined,
            });

            refine(value);
        },
        [attribute, allItems, refine, trackFilter],
    );

    if (!isVisible) return null;

    return (
        <RefinementDropdown
            label={label}
            count={selectedCount}
            searchValue={searchValue}
            onChange={onClose}
            headerRef={headerRef}
            topContent={
                <>
                    {searchable && (
                        <RefinementSearchInput
                            value={searchValue}
                            onChange={(event) => setSearchValue(event.target.value)}
                            onClear={() => setSearchValue('')}
                            placeholder={searchPlaceholder ?? `Søg på ${label}`}
                        />
                    )}
                    <FilterSelectionHeader
                        label={label ?? ''}
                        selectedCount={selectedCount}
                        onClear={clearSelection}
                    />
                </>
            }
        >
            {allItems.length === 0 ? (
                <RefinementEmptyList />
            ) : (
                <div className={styles.listRow}>
                    <div className={styles.listColumn}>
                        <AlphabeticVirtualizedList
                            ref={listRef}
                            groups={groups}
                            itemKey={(item) => `${item.label}-${item.value}`}
                            renderItem={(item, { isSectionEnd }) => (
                                <RefinementListRow
                                    label={item.label}
                                    value={item.value}
                                    count={item.count}
                                    isRefined={item.isRefined}
                                    isCompact
                                    isLastInGroup={isSectionEnd}
                                    shouldShowCount={showCount}
                                    onRefine={handleRefine}
                                />
                            )}
                        />
                    </div>

                    <LetterPicker
                        letters={letters}
                        onSelect={(letter) => listRef.current?.scrollToLetter(letter)}
                        facetHeaderHeight={headerHeight}
                    />
                </div>
            )}
        </RefinementDropdown>
    );
};
